import React, { useEffect, useState } from "react";
import axios from "../../api/axiosConfig";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPlus,
  FaSync,
  FaSearch,
  FaTimes,
  FaEdit,
  FaToggleOn,
  FaToggleOff,
} from "react-icons/fa";

function InsumoList() {
  const [insumos, setInsumos] = useState([]);
  const [insumosFiltrados, setInsumosFiltrados] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filtros
  const [filtroEstado, setFiltroEstado] = useState("Todos"); // OK, BAJO, AGOTADO, Todos
  const [filtroCategoria, setFiltroCategoria] = useState("Todas");
  const [filtroActivo, setFiltroActivo] = useState("Solo activos");
  const [busqueda, setBusqueda] = useState("");

  // Para construir lista de categorías dinámicas
  const [categoriasDisponibles, setCategoriasDisponibles] = useState(["Todas"]);

  // Modal
  const [mostrarModal, setMostrarModal] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [insumoActual, setInsumoActual] = useState({
    id: null,
    nombre: "",
    unidadMedida: "",
    stockMinimo: 0,
    categoria: "",
    activo: true,
  });

  useEffect(() => {
    obtenerInsumos();
  }, []);

  useEffect(() => {
    filtrarInsumos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroEstado, filtroCategoria, filtroActivo, busqueda, insumos]);

  const obtenerInsumos = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/insumos");
      const lista = res.data || [];
      setInsumos(lista);

      // categorías dinámicas
      const cats = new Set(["Todas"]);
      lista.forEach((i) => {
        if (i.categoria) cats.add(i.categoria);
      });
      setCategoriasDisponibles(Array.from(cats));
    } catch (error) {
      console.error("Error al cargar insumos:", error);
      toast.error("❌ Error al cargar los insumos");
    } finally {
      setLoading(false);
    }
  };

  const filtrarInsumos = () => {
    let filtrados = [...insumos];

    // Estado
    if (filtroEstado !== "Todos") {
      filtrados = filtrados.filter(
        (i) => (i.estado || "").toUpperCase() === filtroEstado.toUpperCase()
      );
    }

    // Categoría
    if (filtroCategoria !== "Todas") {
      filtrados = filtrados.filter(
        (i) =>
          (i.categoria || "").toLowerCase() ===
          filtroCategoria.toLowerCase()
      );
    }

    // Activo / inactivo
    if (filtroActivo === "Solo activos") {
      filtrados = filtrados.filter((i) => i.activo === true);
    } else if (filtroActivo === "Solo inactivos") {
      filtrados = filtrados.filter((i) => i.activo === false);
    }

    // Búsqueda por nombre
    if (busqueda.trim() !== "") {
      const q = busqueda.toLowerCase();
      filtrados = filtrados.filter((i) =>
        (i.nombre || "").toLowerCase().includes(q)
      );
    }

    setInsumosFiltrados(filtrados);
  };

  const abrirModalNuevo = () => {
    setInsumoActual({
      id: null,
      nombre: "",
      unidadMedida: "",
      stockMinimo: 0,
      categoria: "",
      activo: true,
    });
    setModoEdicion(false);
    setMostrarModal(true);
  };

  const abrirModalEditar = (insumo) => {
    setInsumoActual({
      id: insumo.id,
      nombre: insumo.nombre || "",
      unidadMedida: insumo.unidadMedida || "",
      stockMinimo: insumo.stockMinimo ?? 0,
      categoria: insumo.categoria || "",
      activo: insumo.activo ?? true,
    });
    setModoEdicion(true);
    setMostrarModal(true);
  };

  const cerrarModal = () => {
    setMostrarModal(false);
  };

  const guardarInsumo = async () => {
    if (!insumoActual.nombre) {
      toast.warning("⚠️ El nombre es obligatorio");
      return;
    }

    try {
      const payload = {
        ...insumoActual,
        stockMinimo: Number(insumoActual.stockMinimo) || 0,
      };

      if (modoEdicion && insumoActual.id) {
        await axios.put(`/insumos/${insumoActual.id}`, payload);
        toast.success("✅ Insumo actualizado correctamente");
      } else {
        await axios.post("/insumos", payload);
        toast.success("✅ Insumo creado correctamente");
      }

      setMostrarModal(false);
      obtenerInsumos();
    } catch (error) {
      console.error("Error al guardar insumo:", error);
      toast.error("❌ No se pudo guardar el insumo");
    }
  };

  const toggleActivoInsumo = async (insumo) => {
    try {
      await axios.patch(`/insumos/${insumo.id}/activo`, null, {
        params: { activo: !insumo.activo },
      });
      toast.success(
        !insumo.activo
          ? "✅ Insumo activado nuevamente"
          : "🚫 Insumo desactivado"
      );
      obtenerInsumos();
    } catch (error) {
      console.error("Error al cambiar estado activo:", error);
      toast.error("❌ No se pudo cambiar el estado del insumo");
    }
  };

  const estadoChip = (estado) => {
    const e = (estado || "").toUpperCase();
    if (e === "AGOTADO") {
      return {
        texto: "🔴 Agotado",
        clase:
          "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
      };
    }
    if (e === "BAJO") {
      return {
        texto: "🟡 Stock bajo",
        clase:
          "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
      };
    }
    if (e === "OK") {
      return {
        texto: "🟢 OK",
        clase:
          "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
      };
    }
    return {
      texto: "Sin estado",
      clase:
        "bg-slate-100 text-slate-700 dark:bg-slate-900/60 dark:text-slate-300",
    };
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="bg-white/95 text-slate-900 dark:bg-slate-900/90 dark:text-slate-100 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-6 mt-3">
        {/* Encabezado con filtros y buscador */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-amber-500 dark:text-amber-300">
            Inventario de Insumos
          </h2>

          <div className="flex flex-wrap items-center gap-2">
            {/* Estado */}
            <select
              className="px-3 py-1.5 text-xs sm:text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400"
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              style={{ minWidth: "150px" }}
            >
              <option value="Todos">Estado (todos)</option>
              <option value="OK">OK</option>
              <option value="BAJO">Bajo</option>
              <option value="AGOTADO">Agotado</option>
            </select>

            {/* Categoría */}
            <select
              className="px-3 py-1.5 text-xs sm:text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400"
              value={filtroCategoria}
              onChange={(e) => setFiltroCategoria(e.target.value)}
              style={{ minWidth: "170px" }}
            >
              {categoriasDisponibles.map((cat, idx) => (
                <option key={idx} value={cat}>
                  {cat === "" ? "Sin categoría" : cat}
                </option>
              ))}
            </select>

            {/* Activo / inactivo */}
            <select
              className="px-3 py-1.5 text-xs sm:text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400"
              value={filtroActivo}
              onChange={(e) => setFiltroActivo(e.target.value)}
              style={{ minWidth: "150px" }}
            >
              <option>Solo activos</option>
              <option>Solo inactivos</option>
              <option>Todos</option>
            </select>

            {/* Buscador */}
            <div className="flex items-center rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 overflow-hidden">
              <span className="pl-2 pr-1 text-slate-400">
                <FaSearch className="w-3.5 h-3.5" />
              </span>
              <input
                type="text"
                placeholder="Buscar insumo..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="px-2 py-1.5 text-xs sm:text-sm bg-transparent text-slate-800 dark:text-slate-100 focus:outline-none"
                style={{ width: "160px" }}
              />
              {busqueda && (
                <button
                  type="button"
                  onClick={() => setBusqueda("")}
                  className="px-2 py-1.5 text-xs text-slate-400 hover:text-red-400"
                >
                  <FaTimes />
                </button>
              )}
            </div>

            {/* Botón Actualizar */}
            <button
              onClick={obtenerInsumos}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-400 dark:border-slate-600 text-xs sm:text-sm text-slate-700 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <FaSync className="text-sky-500" />
              Actualizar
            </button>

            {/* Botón Nuevo insumo */}
            <button
              onClick={abrirModalNuevo}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 transition-colors"
            >
              <FaPlus />
              Nuevo Insumo
            </button>
          </div>
        </div>

        {/* Contador */}
        <div className="flex justify-between items-center mb-3">
          <span className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
            Mostrando {insumosFiltrados.length} insumo
            {insumosFiltrados.length !== 1 ? "s" : ""}{" "}
            {filtroEstado !== "Todos" && `en estado ${filtroEstado}`}{" "}
            {filtroCategoria !== "Todas" &&
              `de la categoría ${filtroCategoria}`}{" "}
            {busqueda && `que coinciden con "${busqueda}"`}
          </span>
        </div>

        {/* Tabla */}
        <div className="mt-2 rounded-xl border border-slate-200 dark:border-slate-800">
          <div className="w-full overflow-x-auto">
            <table className="w-full min-w-[800px] md:min-w-full text-sm">
              <thead className="bg-slate-100 dark:bg-slate-950/80">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-slate-700 dark:text-slate-100">
                    ID
                  </th>
                  <th className="px-4 py-2 text-left font-semibold text-slate-700 dark:text-slate-100">
                    Nombre
                  </th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                    Unidad
                  </th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                    Stock actual
                  </th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                    Stock mínimo
                  </th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                    Estado
                  </th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                    Categoría
                  </th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                    Activo
                  </th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {insumosFiltrados.length === 0 ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="px-4 py-4 text-center text-slate-500 dark:text-slate-400 text-sm"
                    >
                      {loading
                        ? "Cargando insumos..."
                        : "No se encontraron insumos"}
                    </td>
                  </tr>
                ) : (
                  insumosFiltrados.map((i) => {
                    const chip = estadoChip(i.estado);
                    return (
                      <tr
                        key={i.id}
                        className={`odd:bg-white even:bg-slate-50 dark:odd:bg-slate-900/60 dark:even:bg-slate-900/40 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${
                          !i.activo ? "opacity-60" : ""
                        }`}
                      >
                        <td className="px-4 py-2 text-left align-middle">
                          {i.id}
                        </td>
                        <td className="px-4 py-2 text-left align-middle font-medium">
                          {i.nombre}
                        </td>
                        <td className="px-4 py-2 text-center align-middle">
                          {i.unidadMedida || "-"}
                        </td>
                        <td className="px-4 py-2 text-center align-middle">
                          {i.stockActual ?? 0}
                        </td>
                        <td className="px-4 py-2 text-center align-middle">
                          {i.stockMinimo ?? 0}
                        </td>
                        <td className="px-4 py-2 text-center align-middle">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${chip.clase}`}
                          >
                            {chip.texto}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-center align-middle">
                          {i.categoria || "Sin categoría"}
                        </td>
                        <td className="px-4 py-2 text-center align-middle">
                          {i.activo ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                              Activo
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-slate-200 text-slate-700 dark:bg-slate-900/60 dark:text-slate-300">
                              Inactivo
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-center align-middle">
                          <div className="flex flex-wrap justify-center gap-2">
                            <button
                              onClick={() => abrirModalEditar(i)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-sky-500 text-sky-300 hover:bg-sky-500 hover:text-slate-900 transition-colors"
                            >
                              <FaEdit />
                              Editar
                            </button>

                            <button
                              onClick={() => toggleActivoInsumo(i)}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                                i.activo
                                  ? "border-amber-500 text-amber-300 hover:bg-amber-500 hover:text-slate-900"
                                  : "border-emerald-500 text-emerald-300 hover:bg-emerald-500 hover:text-slate-900"
                              }`}
                            >
                              {i.activo ? <FaToggleOff /> : <FaToggleOn />}
                              {i.activo ? "Desactivar" : "Activar"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal Crear/Editar */}
      {mostrarModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:!bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:!bg-slate-800">
              <h3 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100">
                {modoEdicion ? "✏️ Editar Insumo" : "➕ Nuevo Insumo"}
              </h3>
              <button
                onClick={cerrarModal}
                className="p-1 rounded-full border border-slate-300 dark:border-slate-600
                          !bg-slate-100 dark:!bg-slate-800
                          !text-slate-600 dark:!text-slate-200
                          hover:!bg-slate-200 dark:hover:!bg-slate-700 transition-colors"
              >
                <FaTimes />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-4">
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                    Nombre
                  </label>
                  <input
                    type="text"
                    value={insumoActual.nombre}
                    onChange={(e) =>
                      setInsumoActual({
                        ...insumoActual,
                        nombre: e.target.value,
                      })
                    }
                    className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                  />
                </div>

                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                      Unidad de medida
                    </label>
                    <input
                      type="text"
                      value={insumoActual.unidadMedida}
                      onChange={(e) =>
                        setInsumoActual({
                          ...insumoActual,
                          unidadMedida: e.target.value,
                        })
                      }
                      placeholder="g, ml, unidad..."
                      className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                    />
                  </div>
                  <div className="w-32">
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                      Stock mínimo
                    </label>
                    <input
                      type="number"
                      min={0}
                      value={insumoActual.stockMinimo}
                      onChange={(e) =>
                        setInsumoActual({
                          ...insumoActual,
                          stockMinimo: e.target.value,
                        })
                      }
                      className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                    Categoría
                  </label>
                  <input
                    type="text"
                    value={insumoActual.categoria}
                    onChange={(e) =>
                      setInsumoActual({
                        ...insumoActual,
                        categoria: e.target.value,
                      })
                    }
                    placeholder="ALIMENTO, BEBIDA, ENVASE..."
                    className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                  />
                </div>

                <div className="flex items-center gap-2 mt-2">
                  <input
                    id="activo"
                    type="checkbox"
                    checked={insumoActual.activo}
                    onChange={(e) =>
                      setInsumoActual({
                        ...insumoActual,
                        activo: e.target.checked,
                      })
                    }
                    className="h-4 w-4 rounded border-slate-300 text-emerald-500 focus:ring-emerald-500"
                  />
                  <label
                    htmlFor="activo"
                    className="text-xs sm:text-sm text-slate-600 dark:text-slate-300"
                  >
                    Insumo activo
                  </label>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:!bg-slate-800 rounded-b-2xl">
              <button
                onClick={cerrarModal}
                className="px-4 py-2 rounded-lg text-xs sm:text-sm font-medium
                          border border-slate-300 dark:border-slate-600
                          !bg-slate-100 dark:!bg-slate-800
                          !text-slate-700 dark:!text-slate-200
                          hover:!bg-slate-200 dark:hover:!bg-slate-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={guardarInsumo}
                className="px-4 py-2 rounded-lg text-xs sm:text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 transition-colors"
              >
                {modoEdicion ? "💾 Guardar Cambios" : "✅ Registrar Insumo"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default InsumoList;
